import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ArrowLeft, Calendar, Image as ImageIcon } from 'lucide-react'
import {
  useBooking,
  useBookingHistory,
  useCancelBooking,
  useRescheduleBookingJob,
  useCancelBookingJob,
} from '@/hooks/data/useBookings'
import { useAlertContext } from '@/providers/AlertProvider'
import { JobMediaPanel } from '@/components/jobs/JobMediaPanel'

function statusColor(s: string) {
  return (
    {
      Active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      Completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    }[s] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  )
}

function BookingDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const booking = useBooking(id)
  const cancel = useCancelBooking()
  const rescheduleJob = useRescheduleBookingJob(id)
  const cancelJob = useCancelBookingJob(id)
  const { success: showSuccess, error: showError, confirm } = useAlertContext()

  const [reason, setReason] = useState('')
  const [reschedulingJobId, setReschedulingJobId] = useState<string | null>(null)
  const [newStart, setNewStart] = useState('')
  const [rescheduleReason, setRescheduleReason] = useState('')
  const [mediaJobId, setMediaJobId] = useState<string | null>(null)

  const submitReschedule = async (jobId: string) => {
    if (!newStart.trim() || !rescheduleReason.trim()) return
    try {
      await rescheduleJob.mutateAsync({ jobId, newStart: new Date(newStart).toISOString(), reason: rescheduleReason.trim() })
      showSuccess('Rescheduled', 'The visit has been rescheduled.')
      setReschedulingJobId(null)
      setNewStart('')
      setRescheduleReason('')
    } catch (err) {
      showError('Reschedule failed', (err as Error)?.message || 'Failed to reschedule the visit.')
    }
  }

  const cancelOneVisit = (jobId: string) => {
    confirm({
      title: 'Cancel visit',
      message: 'Cancel just this visit? The rest of the booking is unaffected.',
      type: 'warning',
      confirmText: 'Cancel visit',
      cancelText: 'Keep it',
      showCancel: true,
      onConfirm: async () => {
        try {
          await cancelJob.mutateAsync({ jobId, reason: 'Cancelled by admin' })
          showSuccess('Visit cancelled', 'The visit has been cancelled.')
        } catch (err) {
          showError('Cancel failed', (err as Error)?.message || 'Failed to cancel the visit.')
        }
      },
    })
  }

  // Simple page-by-page cursor pagination: the stack holds the cursor used to reach each
  // earlier page, so "Newer" just pops back rather than needing to re-derive anything.
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const currentCursor = cursorStack[cursorStack.length - 1]
  const history = useBookingHistory(id, currentCursor)

  if (booking.isLoading) return <p className="p-6 text-gray-500">Loading booking…</p>
  if (booking.isError || !booking.data) return <p className="p-6 text-red-600">Failed to load booking — requires the manager role, or it doesn't exist.</p>

  const b = booking.data

  return (
    <div className="p-2 md:p-4 space-y-4 max-w-3xl">
      <button
        onClick={() => navigate({ to: '/crm' })}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
      >
        <ArrowLeft className="w-4 h-4" /> Back to customers
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">{b.serviceType} booking</h1>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(b.status)}`}>{b.status}</span>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-600 dark:text-gray-400">
          <div><span className="text-gray-400">Address:</span> {b.addressLine}, {b.suburb}</div>
          <div><span className="text-gray-400">Recurrence:</span> {b.recurrence}</div>
          <div><span className="text-gray-400">Agreed price:</span> ${b.agreedPrice.toFixed(2)}</div>
          <div><span className="text-gray-400">Booking ID:</span> {b.id}</div>
        </dl>

        {b.status === 'Active' && (
          <div className="mt-4 flex items-center gap-2">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Cancellation reason…"
              className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
            />
            <button
              disabled={!reason.trim() || cancel.isPending}
              onClick={async () => {
                try {
                  await cancel.mutateAsync({ id: b.id, reason: reason.trim() })
                  showSuccess('Cancelled', 'Booking has been cancelled.')
                  setReason('')
                } catch (err) {
                  showError('Cancel failed', (err as Error)?.message || 'Failed to cancel booking.')
                }
              }}
              className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-40"
            >
              Cancel Booking
            </button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4" /> Visits ({b.jobs.length})
        </h2>
        {b.jobs.length === 0 ? (
          <p className="text-sm text-gray-500">No visits scheduled yet.</p>
        ) : (
          <ul className="space-y-2">
            {b.jobs.map((j) => {
              const editable = j.status !== 'Completed' && j.status !== 'Cancelled'
              return (
                <li key={j.id} className="border-t border-gray-100 dark:border-gray-700 pt-2 first:border-0 first:pt-0">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {new Date(j.scheduledStart).toLocaleString()} ({j.predictedDurationMinutes} min)
                    </span>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(j.status)}`}>{j.status}</span>
                      <button
                        onClick={() => setMediaJobId(mediaJobId === j.id ? null : j.id)}
                        className="flex items-center gap-1 text-xs text-gray-600 hover:underline dark:text-gray-400"
                      >
                        <ImageIcon className="h-3 w-3" /> Photos
                      </button>
                      {editable && (
                        <>
                          <button
                            onClick={() => setReschedulingJobId(reschedulingJobId === j.id ? null : j.id)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Reschedule
                          </button>
                          <button onClick={() => cancelOneVisit(j.id)} className="text-xs text-red-500 hover:underline">
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {mediaJobId === j.id && <JobMediaPanel jobId={j.id} />}
                  {reschedulingJobId === j.id && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        type="datetime-local"
                        value={newStart}
                        onChange={(e) => setNewStart(e.target.value)}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-2 py-1 text-xs"
                      />
                      <input
                        value={rescheduleReason}
                        onChange={(e) => setRescheduleReason(e.target.value)}
                        placeholder="Reason…"
                        className="flex-1 min-w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-2 py-1 text-xs"
                      />
                      <button
                        disabled={!newStart.trim() || !rescheduleReason.trim() || rescheduleJob.isPending}
                        onClick={() => submitReschedule(j.id)}
                        className="px-2 py-1 rounded-lg bg-blue-600 text-white text-xs disabled:opacity-40"
                      >
                        Confirm
                      </button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Timeline</h2>
        {history.isLoading ? (
          <p className="text-sm text-gray-500">Loading timeline…</p>
        ) : !history.data || history.data.items.length === 0 ? (
          <p className="text-sm text-gray-500">No history recorded.</p>
        ) : (
          <ul className="space-y-3">
            {history.data.items.map((h) => (
              <li key={h.id} className="text-sm border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                <div className="text-gray-800 dark:text-gray-100 font-medium">
                  {h.action}
                  {h.oldStatus && h.newStatus && (
                    <span className="text-gray-500 font-normal"> ({h.oldStatus} → {h.newStatus})</span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(h.createdAt).toLocaleString()}
                  {h.performedBy && ` · ${h.performedBy}`}
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex items-center gap-3">
          {cursorStack.length > 0 && (
            <button onClick={() => setCursorStack((prev) => prev.slice(0, -1))} className="text-sm text-blue-600 hover:underline">
              ← Newer
            </button>
          )}
          {history.data?.hasMore && history.data.nextCursor && (
            <button
              onClick={() => setCursorStack((prev) => [...prev, history.data!.nextCursor!])}
              className="text-sm text-blue-600 hover:underline"
            >
              Older →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/bookings/$id/')({
  component: BookingDetailPage,
})
